import * as React from "react";
import { KeyValue, Query } from "../../../interfaces";
import SODStateList from "./SODStateList";
import { TableRow, TableCell, Button, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { fetchData } from "../../../common/services/ApiClient";
import useOpenCloseAll from "../../../hooks/useOpenCloseAll";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";
import { ApiBaseUrl } from "../../../config";
import {
  arraySum,
  chunkArray,
  removeDuplicateArrayObj,
} from "../../../common/services/Utilities";

interface Props {
  appState: Query;
  openClose: string;
  setOpenClose: (openClose: string) => void;
  openCloseAllDisplay: KeyValue;
  setOpenCloseAllDisplay: (openCloseAll: KeyValue) => void;
}

export default function BHCList({
  appState,
  openClose,
  setOpenClose,
  openCloseAllDisplay,
  setOpenCloseAllDisplay,
}: Props) {
  const [open, setOpen] = React.useState<string[]>([]);
  const [openCloseStates, setOpenCloseStates] = React.useState("");
  const [bhcData, setBhcData] = React.useState<KeyValue[]>([]);
  const [bhcAggResponse, setBhcAggResponse] = React.useState<KeyValue>({
    data: [],
    totals: {},
  });

  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const [openAllButton, setOpenAllButton] = React.useState<KeyValue>({});
  const [marketSelectSumCall, setMakeMarketSelectSumCall] =
    React.useState(false);
  useOpenCloseAll({
    data: bhcData,
    key: "CERT",
    currentValues: open,
    openClose: openClose === "ALL",
    parent: "ALL",
    setValues: setOpen,
    setParent: setOpenClose,
    buttonDisplays: openCloseAllDisplay,
    setButtonDisplays: setOpenCloseAllDisplay,
  });

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      ui: appState.ui.marketSelection,
    };
    if (
      Object.keys(appState.ui?.marketSelection[0]).length > 0 &&
      appState.filters.stateNames[0] !== "ALL" &&
      appState.filters.bhcRSSD &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setOpen([]);
      setPreviousSearch(filtersSearch);
      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const marketSelection = appState.ui.marketSelection[index];
          if (marketSelection?.zips) {
            const zips = chunkArray(marketSelection?.zips, 500);
            return zips.map((zipChunk) =>
              fetchData(
                `${ApiBaseUrl()}/sod`,
                generateBranchOfficeDepositsApiUrl(
                  appState,
                  undefined,
                  state,
                  { ...marketSelection, zips: zipChunk },
                  "CERT",
                ),
              ),
            );
          } else {
            return fetchData(
              `${ApiBaseUrl()}/sod`,
              generateBranchOfficeDepositsApiUrl(
                appState,
                undefined,
                state,
                marketSelection,
                "CERT",
              ),
            );
          }
        });

      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls.flat())
          .then((response) => {
            if (isMounted) {
              const data = response.map((res: any) => res.data);

              setBhcData(
                removeDuplicateArrayObj([].concat.apply([], data), "CERT"),
              );
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setMakeMarketSelectSumCall(true);
            isMounted = false;
          });
      })();
    }
  }, [appState, previousSearch]);

  React.useEffect(() => {
    if (marketSelectSumCall) {
      setOpen([]);
      setMakeMarketSelectSumCall(false);
      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const marketSelection = appState.ui.marketSelection[index];
          const zips = chunkArray(marketSelection?.zips, 500);
          return zips.map((zipChunk) =>
            fetchData(
              `${ApiBaseUrl()}/sod`,
              generateBranchOfficeDepositsApiUrl(
                appState,
                { by: "CERT", sum: ["DEPSUMBR"] },
                state,
                { ...marketSelection, zips: zipChunk },
              ),
            ),
          );
        });

      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls.flat())
          .then((response) => {
            if (isMounted) {
              const data = [].concat.apply(
                [],
                response
                  .map((res: any) => res.data)
                  .filter((resData) => resData),
              );

              const count = arraySum(
                data.map((v: any) => {
                  return { [v.CERT]: v.count };
                }),
              );

              const depsum = arraySum(
                data.map((v: any) => {
                  return { [v.CERT]: v.sum_DEPSUMBR };
                }),
              );

              const newData = Object.keys(count).map((val: any) => {
                return {
                  CERT: val,
                  count: count[val],
                  sum_DEPSUMBR: depsum[val],
                };
              });
              const totals = response.map((res: any) => res.totals);
              setBhcAggResponse({
                data: newData,
                totals: arraySum(totals),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, marketSelectSumCall]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
    };

    if (
      appState.filters.bhcRSSD &&
      (Object.keys(appState.ui.marketSelection[0]).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setOpen([]);
      setPreviousSearch(filtersSearch);
      const fetchUrls = [
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            appState,
            undefined,
            undefined,
            undefined,
            "CERT",
          ),
        ),
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(appState, {
            by: "CERT",
            terms: [""],
            sum: ["DEPSUMBR"],
          }),
        ),
      ];
      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls)
          .then((response) => {
            if (isMounted) {
              setBhcData(response[0].data);
              setBhcAggResponse({
                data: response[1].data,
                totals: response[1].totals,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, previousSearch]);

  const bhcRows = bhcData.map((data, int) => {
    const filteredAggData = bhcAggResponse.data.filter(
      (aggData: KeyValue) => Number(aggData.CERT) === Number(data.CERT),
    )[0];
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        setOpenCloseAllDisplay({
          ...openCloseAllDisplay,
          ALL: {
            ...openCloseAllDisplay.ALL,
            [data.CERT]: !open.includes(data.CERT) ? true : false,
          },
        });
        open.includes(data.CERT) && setOpenAllButton({});
        !open.includes(data.CERT)
          ? setOpen([...open, data.CERT])
          : setOpen(open.filter((institution) => institution !== data.CERT));
      }
    };
    return (
      <React.Fragment key={`${data.CERT}_${int}`}>
        <TableRow
          sx={{
            "& td.MuiTableCell-root.MuiTableCell-body": {
              backgroundColor: "#D9E8F6",
              fontWeight: "bold",
            },
            "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
              {
                cursor: "pointer",
                filter: "brightness(.9)",
              },
          }}
          tabIndex={0}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              expandCollapse(e);
            }
          }}
          onClick={(e: any) => expandCollapse(e)}
        >
          <TableCell
            sx={{
              "&.MuiTableCell-root.MuiTableCell-body": {
                textAlign: "left",
              },
            }}
          >
            <Chip
              sx={{
                "cursor": "pointer",
                "backgroundColor": "transparent",
                "color": "#0000EE",
                "textDecoration": "underline",
                "fontSize": 14,
                "float": "left",
                "& .MuiChip-icon": {
                  color: (theme) => theme.palette.primary.main,
                },
              }}
              label={data.NAMEFULL}
              icon={
                open.includes(data.CERT) ? (
                  <ExpandMoreIcon />
                ) : (
                  <ChevronRightIcon />
                )
              }
            />
          </TableCell>
          <TableCell>
            {openAllButton[data.CERT] &&
              Object.keys(openAllButton[data.CERT]).length > 1 &&
              open.includes(data.CERT) && (
                <Button
                  id={
                    Object.values(openAllButton[data.CERT]).every((val) => val)
                      ? `closeAllStates_${data.CERT}`
                      : `openAllStates_${data.CERT}`
                  }
                  sx={(theme) => ({
                    "minWidth": 80,
                    "margin": 0,
                    "color": theme.palette.primary.main,
                    "fontWeight": 600,
                    "&:hover, &.Mui-focusVisible": {
                      backgroundColor: "#fff",
                    },
                  })}
                  onClick={() => {
                    setOpenCloseStates(data.CERT);
                  }}
                >
                  {Object.values(openAllButton[data.CERT]).every((val) => val)
                    ? "Close All"
                    : "Open All"}
                </Button>
              )}
          </TableCell>
          <TableCell>{data.ADDRESS}</TableCell>
          <TableCell>{data.CITY} </TableCell>
          <TableCell>{data.ZIP}</TableCell>
          <TableCell>{filteredAggData?.count?.toLocaleString()}</TableCell>
          <TableCell>{data.CERT}</TableCell>
          <TableCell>{data.BKCLASS}</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell style={{ textAlign: "right" }}>
            {filteredAggData?.sum_DEPSUMBR?.toLocaleString()}
          </TableCell>
        </TableRow>
        {open.includes(data.CERT) && (
          <SODStateList
            appState={appState}
            cert={data.CERT}
            fromBHC
            parentName={data.CERT}
            openClose={openCloseStates}
            setOpenClose={setOpenCloseStates}
            openCloseAllDisplay={openAllButton}
            setOpenCloseAllDisplay={setOpenAllButton}
          />
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {bhcRows}
      <TableRow
        sx={{
          "& td.MuiTableCell-root.MuiTableCell-body": {
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "#ffffff",
            fontWeight: "bold",
          },
        }}
      >
        <TableCell style={{ textAlign: "left", paddingLeft: "1rem" }}>
          Totals
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell>{bhcAggResponse.totals?.count?.toLocaleString()}</TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell style={{ textAlign: "right" }}>
          {bhcAggResponse.totals?.sum_DEPSUMBR?.toLocaleString()}
        </TableCell>
      </TableRow>
    </>
  );
}
